import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTracker } from '../../state/store';
import { envelopeValueAt } from '../../engine/player/player';
import type { Envelope } from '../../engine/types';
import { Button, Field, NumberInput, Panel, Toggle } from './primitives';

/**
 * Volume and panning envelope editor.
 *
 * The x axis is ticks, not time: an envelope point at x=24 arrives 24 ticks
 * after the note starts, so the same envelope moves faster at a higher tempo.
 * That is how XM defines it, and it is worth showing the unit for that reason.
 */
function EnvelopeCanvas({
  envelope,
  colour,
  onChange,
}: {
  envelope: Envelope;
  colour: string;
  onChange: () => void;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState({ width: 400, height: 110 });
  const dragIndex = useRef<number | null>(null);

  const MAX_X = 324;
  const MAX_Y = 64;

  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const observer = new ResizeObserver(() => setSize({ width: el.clientWidth, height: el.clientHeight }));
    observer.observe(el);
    setSize({ width: el.clientWidth, height: el.clientHeight });
    return () => observer.disconnect();
  }, []);

  const toScreen = useCallback(
    (x: number, y: number) => ({
      sx: (x / MAX_X) * size.width,
      sy: size.height - (y / MAX_Y) * size.height,
    }),
    [size],
  );

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    const { width, height } = size;
    canvas.width = Math.max(1, width * dpr);
    canvas.height = Math.max(1, height * dpr);
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    ctx.fillStyle = '#0b0b0e';
    ctx.fillRect(0, 0, width, height);

    // Grid every 16 ticks, with a brighter line at the halfway level.
    ctx.strokeStyle = '#181820';
    ctx.lineWidth = 1;
    for (let x = 0; x <= MAX_X; x += 16) {
      const sx = (x / MAX_X) * width;
      ctx.beginPath();
      ctx.moveTo(sx, 0);
      ctx.lineTo(sx, height);
      ctx.stroke();
    }
    ctx.strokeStyle = '#22222a';
    ctx.beginPath();
    ctx.moveTo(0, height / 2);
    ctx.lineTo(width, height / 2);
    ctx.stroke();

    if (!envelope.enabled) {
      ctx.fillStyle = '#3d3d4a';
      ctx.font = '11px "Spline Sans Mono", monospace';
      ctx.textAlign = 'center';
      ctx.fillText('envelope disabled', width / 2, height / 2 - 6);
      ctx.textAlign = 'left';
      return;
    }

    // Sustain and loop markers.
    const marker = (index: number, label: string, markerColour: string) => {
      const point = envelope.points[index];
      if (!point) return;
      const { sx } = toScreen(point.x, 0);
      ctx.strokeStyle = markerColour;
      ctx.setLineDash([3, 3]);
      ctx.beginPath();
      ctx.moveTo(sx, 0);
      ctx.lineTo(sx, height);
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.fillStyle = markerColour;
      ctx.font = 'bold 9px "Spline Sans Mono", monospace';
      ctx.fillText(label, sx + 3, 10);
    };
    if (envelope.sustainEnabled) marker(envelope.sustainPoint, 'S', '#ffb545');
    if (envelope.loopEnabled) {
      marker(envelope.loopStart, 'L', '#7aa2ff');
      marker(envelope.loopEnd, 'E', '#7aa2ff');
    }

    // The curve, sampled at tick resolution so it matches playback exactly.
    ctx.strokeStyle = colour;
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    for (let x = 0; x <= MAX_X; x++) {
      const y = envelopeValueAt(envelope, x);
      const { sx, sy } = toScreen(x, y);
      if (x === 0) ctx.moveTo(sx, sy);
      else ctx.lineTo(sx, sy);
    }
    ctx.stroke();

    for (const [i, point] of envelope.points.entries()) {
      const { sx, sy } = toScreen(point.x, point.y);
      ctx.fillStyle = colour;
      ctx.beginPath();
      ctx.arc(sx, sy, 3.5, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = '#000';
      ctx.beginPath();
      ctx.arc(sx, sy, 1.5, 0, Math.PI * 2);
      ctx.fill();
      void i;
    }
  }, [envelope, size, colour, toScreen]);

  useEffect(() => {
    draw();
  }, [draw]);

  const pointAt = (clientX: number, clientY: number) => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    const x = clientX - rect.left;
    const y = clientY - rect.top;
    for (const [i, point] of envelope.points.entries()) {
      const { sx, sy } = toScreen(point.x, point.y);
      if (Math.hypot(sx - x, sy - y) < 8) return i;
    }
    return null;
  };

  const valueAt = (clientX: number, clientY: number) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    return {
      x: Math.round(((clientX - rect.left) / rect.width) * MAX_X),
      y: Math.round((1 - (clientY - rect.top) / rect.height) * MAX_Y),
    };
  };

  const onPointerDown = (event: React.PointerEvent) => {
    if (!envelope.enabled) return;
    const index = pointAt(event.clientX, event.clientY);

    if (event.altKey && index !== null) {
      // Alt-click removes a point, but the first and last anchor the envelope.
      if (envelope.points.length > 2 && index !== 0) {
        envelope.points.splice(index, 1);
        onChange();
      }
      return;
    }

    if (index === null) {
      if (envelope.points.length >= 12) return;
      const { x, y } = valueAt(event.clientX, event.clientY);
      const at = envelope.points.findIndex((p) => p.x > x);
      const insertAt = at === -1 ? envelope.points.length : at;
      envelope.points.splice(insertAt, 0, { x, y: Math.max(0, Math.min(64, y)) });
      dragIndex.current = insertAt;
      onChange();
    } else {
      dragIndex.current = index;
    }
    (event.target as HTMLElement).setPointerCapture(event.pointerId);
  };

  const onPointerMove = (event: React.PointerEvent) => {
    const index = dragIndex.current;
    if (index === null) return;
    const { x, y } = valueAt(event.clientX, event.clientY);
    const point = envelope.points[index];
    if (!point) return;

    // Points must stay in ascending x order or the envelope stops being a
    // function; the first point is pinned to zero as XM requires.
    const prev = envelope.points[index - 1];
    const next = envelope.points[index + 1];
    const minX = index === 0 ? 0 : (prev?.x ?? 0) + 1;
    const maxX = index === 0 ? 0 : (next?.x ?? MAX_X) - 1;

    point.x = Math.max(minX, Math.min(Math.max(minX, maxX), x));
    point.y = Math.max(0, Math.min(64, y));
    onChange();
  };

  return (
    <div ref={containerRef} className="h-[110px] w-full overflow-hidden rounded border border-ink-700">
      <canvas
        ref={canvasRef}
        style={{ width: size.width, height: size.height }}
        className="block cursor-crosshair"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={() => (dragIndex.current = null)}
      />
    </div>
  );
}

export function EnvelopeEditor() {
  const song = useTracker((s) => s.song);
  const currentInstrument = useTracker((s) => s.currentInstrument);
  const revision = useTracker((s) => s.revision);
  const replaceInstrument = useTracker((s) => s.replaceInstrument);
  const [which, setWhich] = useState<'volume' | 'panning'>('volume');

  const instrument = song.instruments[currentInstrument - 1];
  if (!instrument) {
    return (
      <Panel title="Envelope">
        <p className="font-mono text-[11px] text-ink-500">No instrument selected.</p>
      </Panel>
    );
  }

  const envelope = which === 'volume' ? instrument.volumeEnvelope : instrument.panningEnvelope;
  const commit = () => replaceInstrument(currentInstrument - 1, instrument);

  const isMod = song.format === 'mod';

  return (
    <Panel
      title="Envelope"
      actions={
        <div className="flex gap-1">
          <Button active={which === 'volume'} onClick={() => setWhich('volume')}>
            Vol
          </Button>
          <Button active={which === 'panning'} onClick={() => setWhich('panning')}>
            Pan
          </Button>
        </div>
      }
    >
      <div key={`${revision}-${which}`} className="flex flex-col gap-2">
        {isMod && (
          <p className="rounded border border-amber-400/30 bg-amber-400/5 px-2 py-1 font-mono text-[10px] leading-relaxed text-amber-400">
            This is a MOD. Envelopes are a FastTracker II feature and will not be saved —
            convert the song to XM to keep them.
          </p>
        )}

        <EnvelopeCanvas
          envelope={envelope}
          colour={which === 'volume' ? '#4ceb9b' : '#7aa2ff'}
          onChange={commit}
        />

        <p className="font-mono text-[10px] text-ink-500">
          Click to add a point, drag to move, Alt-click to remove. X is ticks from note start.
        </p>

        <Toggle
          checked={envelope.enabled}
          label="Enabled"
          onChange={(v) => {
            envelope.enabled = v;
            commit();
          }}
        />
        <Toggle
          checked={envelope.sustainEnabled}
          label="Sustain"
          onChange={(v) => {
            envelope.sustainEnabled = v;
            commit();
          }}
        />
        <Toggle
          checked={envelope.loopEnabled}
          label="Loop"
          onChange={(v) => {
            envelope.loopEnabled = v;
            commit();
          }}
        />

        <div className="grid grid-cols-2 gap-x-4">
          <Field label="Sustain pt">
            <NumberInput
              value={envelope.sustainPoint}
              min={0}
              max={Math.max(0, envelope.points.length - 1)}
              onChange={(v) => {
                envelope.sustainPoint = v;
                commit();
              }}
            />
          </Field>
          <Field label="Loop start">
            <NumberInput
              value={envelope.loopStart}
              min={0}
              max={Math.max(0, envelope.points.length - 1)}
              onChange={(v) => {
                envelope.loopStart = v;
                commit();
              }}
            />
          </Field>
          <Field label="Loop end">
            <NumberInput
              value={envelope.loopEnd}
              min={0}
              max={Math.max(0, envelope.points.length - 1)}
              onChange={(v) => {
                envelope.loopEnd = v;
                commit();
              }}
            />
          </Field>
          <Field label="Fadeout">
            <NumberInput
              value={instrument.volumeFadeout}
              min={0}
              max={32767}
              width="w-20"
              onChange={(v) => {
                instrument.volumeFadeout = v;
                commit();
              }}
            />
          </Field>
        </div>

        <h3 className="mt-1 font-mono text-[10px] uppercase tracking-[0.18em] text-ink-500">
          Auto vibrato
        </h3>
        <div className="grid grid-cols-2 gap-x-4">
          <Field label="Depth">
            <NumberInput
              value={instrument.vibratoDepth}
              min={0}
              max={15}
              onChange={(v) => {
                instrument.vibratoDepth = v;
                commit();
              }}
            />
          </Field>
          <Field label="Rate">
            <NumberInput
              value={instrument.vibratoRate}
              min={0}
              max={63}
              onChange={(v) => {
                instrument.vibratoRate = v;
                commit();
              }}
            />
          </Field>
          <Field label="Sweep">
            <NumberInput
              value={instrument.vibratoSweep}
              min={0}
              max={255}
              onChange={(v) => {
                instrument.vibratoSweep = v;
                commit();
              }}
            />
          </Field>
          <Field label="Type">
            <NumberInput
              value={instrument.vibratoType}
              min={0}
              max={3}
              onChange={(v) => {
                instrument.vibratoType = v;
                commit();
              }}
            />
          </Field>
        </div>
      </div>
    </Panel>
  );
}
